import React, { ReactElement, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AlertRow from './AlertRow';
import { useData } from '../../context/data.context';
import Loader from '../loader';
import { Alert } from '../../constants/telosair-interfaces';
import CheckboxControl from '../../controls/Checkbox';
import ExpandedAlertRow from './ExpandedAlertRow';

type Props = {
  showHeading: boolean;
  onExpandToggle: () => void;
  allSelected: boolean;
  setAllSelected: () => void;
};

/**
 * Component for Alert Table Head.
 * @param props - Properties of TableHead.
 * @returns ReactElement.
 */
function TableHead(props: Props): ReactElement {
  const { showHeading, onExpandToggle, allSelected, setAllSelected } = props;
  return (
    <thead>
      <tr className="w-full text-gray-400 text-xs font-medium bg-container-700">
        <th className="pl-6 py-3">
          <div className="m-0 p-0">
            <CheckboxControl
              hideMarginLeft
              id="alert-head"
              checked={allSelected}
              onChange={setAllSelected}
            />
          </div>
        </th>
        <th colSpan={!showHeading ? 6 : 1} className="px-6 py-3 text-left">
          Alert
        </th>
        <th style={{ visibility: showHeading ? 'visible' : 'hidden' }} className="text-center">
          Status
        </th>
        <th style={{ visibility: showHeading ? 'visible' : 'hidden' }} className="text-center">
          Time
        </th>
        <th style={{ visibility: showHeading ? 'visible' : 'hidden' }} className="text-center">
          Duration
        </th>
        <th style={{ visibility: showHeading ? 'visible' : 'hidden' }} className="text-center">
          Device
        </th>
        <th style={{ visibility: showHeading ? 'visible' : 'hidden' }} className="text-center">
          Emailed
        </th>
        <th
          style={{ visibility: showHeading ? 'visible' : 'hidden' }}
          className="text-center max-w-[5%]"
        >
          Tags
        </th>
        <th className="text-left px-9 ">
          <button
            type="button"
            aria-label="Action button"
            className="text-md cursor-pointer flex items-center justify-end w-full"
            onClick={() => onExpandToggle()}
          >
            <div style={{ display: showHeading ? 'block' : 'none' }} className="text-end">
              Action{' '}
            </div>
          </button>
        </th>
      </tr>
    </thead>
  );
}

type AlertsTableProps = {
  allAlerts: Alert[];
  onSelectAlert: (alert: Alert | null) => void;
  checkedAlerts: string[];
  setCheckedAlerts: React.Dispatch<React.SetStateAction<string[]>>;
  /** True while the page is fetching this list (search, filters, date range). */
  loading?: boolean;
};

/**
 * A Users Table Component.
 * @param props - Properties of AlertsTable.
 * @returns - A React Component.
 */
export default function AlertsTable(props: AlertsTableProps): ReactElement {
  const { allAlerts, onSelectAlert, checkedAlerts, setCheckedAlerts, loading: fetching } = props;
  const { loading: contextLoading } = useData();
  // The page fetches its own list; without its flag the table showed
  // "No alerts!" while a search or filter change was still loading.
  const loading = fetching || contextLoading;
  const [expandToday, setExpandToday] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [allSelected, setAllSelected] = useState(false);
  // const [expandPast, setExpandPast] = useState(true);

  const alerts = allAlerts.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  /**
   * Function to check if table is expanded.
   * @param type - Type of table.
   * @returns - Expanded or not.
   */
  const showHeadings = (type: 'today' | 'past'): boolean => {
    if (id) {
      return false;
    }
    if (type === 'today' && expandToday) {
      return true;
    }

    return false;
  };

  // eslint-disable-next-line jsdoc/require-jsdoc
  const handleAlertSelect = (alertId: string): void => {
    if (checkedAlerts.includes(alertId)) {
      setCheckedAlerts((prev) => prev.filter((item) => item !== alertId));
      return;
    }
    setCheckedAlerts((prev) => [...prev, alertId]);
  };

  useEffect(() => {
    if (allSelected) {
      setCheckedAlerts(allAlerts.map((item) => item.id));
    } else {
      setCheckedAlerts([]);
    }
  }, [allSelected]);

  useEffect(() => {
    if (id && allAlerts.length > 0) {
      onSelectAlert(allAlerts.filter((item) => item.id === id)[0]);
    } else {
      onSelectAlert(null);
    }
  }, [allAlerts, id]);

  if (loading) {
    return (
      <div className="flex flex-1 min-h-[16rem] justify-center items-center bg-container-700 rounded">
        <Loader />
      </div>
    );
  }

  const expandedAlert = allAlerts.filter((item) => item.id === id);
  if (id && expandedAlert.length > 0) {
    return <ExpandedAlertRow onClick={() => setSearchParams()} alert={expandedAlert[0]} />;
  }
  return (
    <div>
      <table className="w-full bg-container-700">
        <TableHead
          allSelected={allSelected}
          setAllSelected={() => setAllSelected((prev) => !prev)}
          showHeading={showHeadings('today')}
          onExpandToggle={() => setExpandToday((prev) => !prev)}
        />
        {expandToday && (
          <tbody className="mb-3">
            {alerts.length === 0 && (
              <tr>
                <td className="text-center py-5 bg-container-500 text-gray-500 text-xs" colSpan={9}>
                  No alerts!
                </td>
              </tr>
            )}
            {alerts.map((alert) => (
              // eslint-disable-next-line react/no-array-index-key
              <AlertRow
                onChange={() => handleAlertSelect(alert.id)}
                selected={checkedAlerts.includes(alert.id)}
                alert={alert}
                expandToday={id === alert.id}
                setExpandToday={(val) => {
                  if (val) {
                    setSearchParams({ id: alert.id });
                  } else {
                    setSearchParams();
                  }
                }}
                key={alert.id}
              />
            ))}
          </tbody>
        )}
        {/* <tbody>
          <tr className="bg-background-500">
            <td aria-label="space" colSpan={7} className="h-5" />
          </tr>
        </tbody>
        <TableHead
          label="Past"
          expanded={expandPast}
          showHeading={showHeadings('past')}
          onExpandToggle={() => setExpandPast((prev) => !prev)}
        />
        {expandPast && (
          <tbody>
            {pastAlerts.length === 0 && (
              <tr>
                <td className="text-center py-5 bg-container-500 text-gray-500 text-xs" colSpan={7}>
                  No alerts in past!
                </td>
              </tr>
            )}
            {pastAlerts.map((alert, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <AlertRow onSelectAlert={onSelectAlert} alert={alert} key={`${index}alert`} />
            ))}
          </tbody>
        )} */}
      </table>
    </div>
  );
}
